// src/features/blog/BlogList.jsx
import React, { useEffect, useState } from "react";
import "./blog-admin.css"; // styles for toolbar, grid, buttons, pagination, etc.
import BlogForm from "./BlogForm";
import BlogCard from "./BlogCard";

const API_BASE = "https://api.ainfotech.com.np";

export default function BlogList() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("published");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const limit = 12;

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/blog?status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed");
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Delete failed");
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  // Show pagination only if needed:
  // - when we’re not on the first page
  // - or when the current page is "full" (we likely have more pages)
  const showPager = page > 1 || (items?.length || 0) === limit;

  return (
    <div className="blog-admin">
      {/* Sticky toolbar */}
      <div className="toolbar">
        <h2 className="toolbar__title">Blog Posts</h2>
        <div className="toolbar__actions">
          <select
            className="sel"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="published">published</option>
            <option value="draft">draft</option>
            <option value="">all</option>
          </select>

          <button className="btn btn--cta" onClick={openCreate} title="Create New Post">
            <span className="i i-plus" aria-hidden="true" />
            New Post
          </button>
        </div>
      </div>

      {/* Form panel */}
      {showForm ? (
        <div className="panel">
          <div className="panel__head">
            <h3 className="panel__title">{editing ? "Edit Post" : "Create Post"}</h3>
            {/* <button className="btn btn--close" onClick={closeForm}>
              Close
            </button> */}
          </div>

          <BlogForm
            initial={editing}
            onSaved={() => {
              closeForm();
              load();
            }}
            onCancel={closeForm}
          />
        </div>
      ) : (
        <>
          {/* List / Empty / Loading */}
          {loading ? (
            <div className="empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">📝</div>
              <div className="empty__title">No posts found</div>
              <div className="empty__desc">Try changing the filter or create a new post.</div>
              <button className="btn btn--cta" onClick={openCreate}>
                <span className="i i-plus" /> Create your first post
              </button>
            </div>
          ) : (
            <div className="grid">
              {items.map((it) => (
                <BlogCard
                  key={it.id}
                  item={it}
                  onEdit={setEditing}
                  onDelete={onDelete}
                  openForm={() => setShowForm(true)}
                />
              ))}
            </div>
          )}

          {/* Pagination (only if needed) */}
          {showPager && (
            <div className="pager">
              <button
                className="pill"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <span className="i i-left" aria-hidden="true" />
                Prev
              </button>

              <span className="badge">Page {page}</span>

              <button className="pill" onClick={() => setPage((p) => p + 1)}>
                Next
                <span className="i i-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
